/**
 * SeamEditor — guided micro-correction of ONE audio split (the seam AFTER a scene).
 *
 * The split is computed server-side (silence-aware) and persisted on the slide as
 * `slide.seam`. Most seams land cleanly; this editor surfaces only the ones that
 * need attention and lets the user nudge the boundary inside a focused ±window,
 * magnetically snapping to the detected pauses so they can't land mid-word. On
 * "Apply split" the exact timestamp is sent straight to the deterministic
 * /audio/split endpoint (no chat orchestrator); the worker re-slices via the same
 * splitMasterAudioCore the agent uses and the storyboard refreshes over SSE.
 */
import { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Scissors, Play, Pause, Check, RotateCcw, AlertTriangle, Loader2 } from 'lucide-react';
import type { SeamMeta } from '@/types/outline';
import { queueAudioSplit } from '@/services/audioService';

interface Props {
  conversationId?: string;
  /** The slide whose END this seam is (split_master_audio keys locks by this id). */
  beforeSlideId: string;
  seam: SeamMeta;
  /** Master audio signed URL for previewing the window (optional). */
  seamAudioUrl?: string;
  isReadOnly?: boolean;
}

const SNAP_THRESHOLD = 0.08; // seconds — within this of a pause midpoint, snap to it
const PREVIEW_LEAD = 2.5;    // seconds played BEFORE the cut; preview stops AT the cut

const fmt = (s: number) => {
  const m = Math.floor(s / 60);
  const sec = (s % 60).toFixed(1).padStart(4, '0');
  return `${m}:${sec}`;
};

export function SeamEditor({ conversationId, beforeSlideId, seam, seamAudioUrl, isReadOnly }: Props) {
  // Deterministic apply state (no chat queue): in-flight, just-applied, error.
  const [applying, setApplying] = useState(false);
  const [justApplied, setJustApplied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { start: winStart, end: winEnd } = seam.window;
  const span = Math.max(0.001, winEnd - winStart);

  // The cut follows the persisted seam; it re-syncs when the worker re-splits and
  // the outline refreshes over SSE (seam.cutTime updates to the applied value).
  const [cut, setCut] = useState<number>(seam.cutTime);
  useEffect(() => { setCut(seam.cutTime); }, [seam.cutTime]);

  // Expand low-confidence seams by default; collapse clean ones.
  const [open, setOpen] = useState<boolean>(seam.needsAttention);

  const barRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const rafRef = useRef<number | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [playPct, setPlayPct] = useState<number | null>(null);

  // Prefer the sample-accurate per-seam window WAV (played window-relative); fall back to the
  // master URL (master-time) for outlines split before window export existed. Seeking a WAV
  // is sample-accurate, so preview lines up with the waveform — unlike master-MP3 seeking.
  const audioUrl = seam.windowAudioUrl ?? seamAudioUrl;
  const windowRelative = !!seam.windowAudioUrl;

  const toPct = (t: number) => `${((t - winStart) / span) * 100}%`;

  // Does the current cut sit inside a detected pause? (mid-word otherwise.)
  const onPause = useMemo(
    () => seam.silenceGaps.find((g) => cut >= g.start && cut <= g.end),
    [seam.silenceGaps, cut],
  );

  const snap = useCallback(
    (t: number) => {
      let best: number | null = null;
      let bestD = SNAP_THRESHOLD;
      for (const g of seam.silenceGaps) {
        const d = Math.abs(g.mid - t);
        if (d < bestD) { bestD = d; best = g.mid; }
      }
      return best ?? t;
    },
    [seam.silenceGaps],
  );

  const clamp = (t: number) => Math.min(winEnd - 0.01, Math.max(winStart + 0.01, t));

  const xToTime = (clientX: number) => {
    const el = barRef.current;
    if (!el) return cut;
    const r = el.getBoundingClientRect();
    const frac = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
    return clamp(winStart + frac * span);
  };

  const dragging = useRef(false);
  const onDown = (e: React.PointerEvent) => {
    if (isReadOnly || applying) return;
    dragging.current = true;
    setJustApplied(false);
    setError(null);
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    setCut(snap(xToTime(e.clientX)));
  };
  const onMove = (e: React.PointerEvent) => {
    if (!dragging.current) return;
    setCut(snap(xToTime(e.clientX)));
  };
  const onUp = (e: React.PointerEvent) => {
    if (!dragging.current) return;
    dragging.current = false;
    (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
  };

  const stopPreview = useCallback(() => {
    const a = audioRef.current;
    if (a) a.pause();
    if (rafRef.current != null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    setPreviewing(false);
    setPlayPct(null);
  }, []);

  const preview = () => {
    const a = audioRef.current;
    if (!a || !audioUrl) return;
    if (previewing) { stopPreview(); return; }
    const localCut = windowRelative ? cut - winStart : cut; // window WAV is 0..span
    // Play UP TO the cut and stop there — this is exactly how the split will sound
    // (the slide's audio ends at the blue mark).
    const from = Math.max(0, localCut - PREVIEW_LEAD);
    const to = localCut;
    a.currentTime = from;
    setPreviewing(true);
    a.play().catch(() => stopPreview());
    const tick = () => {
      const cur = windowRelative ? a.currentTime : a.currentTime - winStart;
      setPlayPct(cur / span);
      if (a.paused || a.currentTime >= to) { stopPreview(); return; }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };
  useEffect(() => stopPreview, [stopPreview]);

  const dirty = Math.abs(cut - seam.cutTime) > 0.005;

  const apply = async () => {
    if (!conversationId || !dirty || applying) return;
    const cutTime = parseFloat(cut.toFixed(3));
    setApplying(true);
    setError(null);
    try {
      // Deterministic: straight to the worker via the split endpoint, no orchestrator.
      await queueAudioSplit(conversationId, [{ outlineSlideId: beforeSlideId, cutTime }]);
      setJustApplied(true);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to apply split');
    } finally {
      setApplying(false);
    }
  };

  const revert = () => {
    setCut(seam.cutTime);
    setJustApplied(false);
    setError(null);
  };

  // Collapsed: a thin status row that invites attention only when needed.
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="mt-2 w-full flex items-center gap-2 text-[11px] text-left rounded-md px-2 py-1 text-gray-400 dark:text-zinc-500 hover:bg-gray-50 dark:hover:bg-white/[0.03] transition-colors"
      >
        <Scissors className="w-3 h-3" />
        <span className="tabular-nums">Split at {fmt(cut)}</span>
        {justApplied ? (
          <span className="text-emerald-600 dark:text-emerald-400">· re-splitting…</span>
        ) : (
          <span className="text-emerald-600/80 dark:text-emerald-400/80">· clean cut</span>
        )}
        <span className="ml-auto opacity-60">adjust</span>
      </button>
    );
  }

  return (
    <div className={cn(
      'mt-2 rounded-lg border p-2.5 text-[12px]',
      seam.needsAttention && !justApplied
        ? 'border-amber-300/70 bg-amber-50/50 dark:border-amber-400/30 dark:bg-amber-400/[0.06]'
        : 'border-gray-200 dark:border-zinc-800 bg-gray-50/60 dark:bg-white/[0.02]',
    )}>
      <div className="flex items-center gap-1.5">
        <Scissors className="w-3.5 h-3.5 text-gray-500 dark:text-zinc-400" />
        <span className="font-medium text-gray-700 dark:text-zinc-200">Split to next scene</span>
        {seam.needsAttention && !justApplied && (
          <span className="inline-flex items-center gap-1 text-[10px] text-amber-700 dark:text-amber-300">
            <AlertTriangle className="w-3 h-3" /> needs attention
          </span>
        )}
        <span className="ml-auto tabular-nums text-gray-500 dark:text-zinc-400">{fmt(cut)}</span>
      </div>

      {/* Focused window: silence regions shaded, draggable cut handle, word context */}
      <div className="mt-2 select-none">
        <div
          ref={barRef}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          className="relative h-9 rounded-md bg-gray-200/70 dark:bg-zinc-800 cursor-ew-resize touch-none overflow-hidden"
        >
          {/* Real audio waveform across the window (peaks 0..1, left→right) */}
          {seam.peaks && seam.peaks.length > 0 && (
            <svg className="absolute inset-0 w-full h-full" viewBox={`0 0 ${seam.peaks.length} 100`} preserveAspectRatio="none" aria-hidden="true">
              {seam.peaks.map((p, i) => {
                const h = Math.max(1.5, p * 88);
                return <rect key={i} x={i + 0.12} y={(100 - h) / 2} width={0.76} height={h} rx={0.3} className="fill-gray-400/70 dark:fill-zinc-500/60" />;
              })}
            </svg>
          )}
          {seam.silenceGaps.map((g, i) => (
            <div
              key={i}
              className="absolute top-0 bottom-0 bg-emerald-400/25 dark:bg-emerald-400/20"
              style={{ left: toPct(g.start), width: `${((Math.min(g.end, winEnd) - Math.max(g.start, winStart)) / span) * 100}%` }}
              title={`${g.durationMs}ms pause`}
            />
          ))}
          {/* Playback head during preview */}
          {playPct != null && (
            <div className="absolute top-0 bottom-0 w-px bg-rose-500/90 pointer-events-none" style={{ left: `${Math.min(100, Math.max(0, playPct * 100))}%` }} />
          )}
          {/* The cut handle */}
          <div className="absolute top-0 bottom-0 w-0.5 -ml-px bg-primary" style={{ left: toPct(cut) }}>
            <div className="absolute -top-0.5 left-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-primary ring-2 ring-white dark:ring-zinc-900" />
          </div>
        </div>
        {/* Words on each side for read context */}
        <div className="mt-1 flex justify-between gap-3 text-[10px] leading-tight text-gray-500 dark:text-zinc-500">
          <span className="truncate">…{seam.leadingWords.slice(-5).map((w) => w.word).join(' ')}</span>
          <span className="truncate text-right">{seam.trailingWords.slice(0, 5).map((w) => w.word).join(' ')}…</span>
        </div>
      </div>

      <div className="mt-2 flex items-center gap-1.5">
        <span className={cn(
          'text-[10px] inline-flex items-center gap-1',
          onPause ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300',
        )}>
          {onPause ? <Check className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
          {onPause ? `on a ${onPause.durationMs}ms pause` : 'mid-sentence — drag to a green pause'}
        </span>

        <div className="ml-auto flex items-center gap-1">
          {audioUrl && (
            <button onClick={preview} className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] text-gray-600 dark:text-zinc-300 hover:bg-gray-100 dark:hover:bg-zinc-800">
              {previewing ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />} seam
            </button>
          )}
          {dirty && !applying && (
            <button onClick={revert} className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] text-gray-500 hover:bg-gray-100 dark:hover:bg-zinc-800">
              <RotateCcw className="w-3 h-3" /> reset
            </button>
          )}
          {!isReadOnly && (
            <button
              onClick={apply}
              disabled={!dirty || applying}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
            >
              {applying ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              {applying ? 'Applying…' : 'Apply split'}
            </button>
          )}
        </div>
      </div>

      {justApplied && !dirty && (
        <div className="mt-1.5 text-[10px] text-emerald-700 dark:text-emerald-300">
          Applied — the audio is re-splitting at this exact time; the storyboard will refresh in a moment.
        </div>
      )}
      {error && (
        <div className="mt-1.5 text-[10px] text-rose-600 dark:text-rose-400">
          {error}
        </div>
      )}

      {audioUrl && <audio ref={audioRef} src={audioUrl} preload="none" className="hidden" />}
    </div>
  );
}
